import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Card, 
  Button, 
  Space, 
  Typography, 
  Tag, 
  message, 
  Input,
  DatePicker,
  Row,
  Col,
  Statistic,
  Modal,
  Form,
  Select,
  Checkbox,
  List,
  Tooltip,
  Popconfirm
} from 'antd';
import { 
  CheckCircleOutlined,
  SearchOutlined,
  ReloadOutlined,
  UserOutlined,
  PhoneOutlined,
  FileTextOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import withRoleAccess from '../../hoc/withRoleAccess';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import moment from 'moment';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const CompletedStudentsTracker = () => {
  const { t } = useTranslation();
  const [completedStudents, setCompletedStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);
  const [notesModal, setNotesModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentNotes, setStudentNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteForm] = Form.useForm();
  const [editingNote, setEditingNote] = useState(null);
  const [noteCounts, setNoteCounts] = useState({});
     const token = localStorage.getItem("token");
            let userRole = null;
            let userBranchId = null;
        
            if (token) {
            try {
                const decoded = jwtDecode(token);
                userRole = decoded.role;
                // Support both formats just in case
                userBranchId = decoded.branchId || decoded.branch_id || null;
            } catch (err) {
                console.error("Invalid token", err);
            }
            }
  const API_URL = process.env.REACT_APP_API_URL;

    useEffect(() => {
      const fetchBranches = async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`${API_URL}/branch`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setBranches(res.data);
        } catch (err) {
          console.error("Failed to fetch branches", err);
        }
      };

      fetchBranches();
    }, [API_URL]);

  // Fetch note counts for all students
  const fetchNoteCounts = async (studentIds) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/student-tracking/students/notes-count`, {
        studentIds
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNoteCounts(response.data);
    } catch (error) {
      console.error('Error fetching note counts:', error);
    }
  };

  // Fetch student notes
  const fetchStudentNotes = async (studentId) => {
    setNotesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/student-tracking/student/${studentId}/notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudentNotes(response.data);
    } catch (error) {
      console.error('Error fetching student notes:', error);
      message.error('Failed to fetch notes');
    } finally {
      setNotesLoading(false);
    }
  };

  // Create or update note
  const saveNote = async (values) => {
    try {
      const token = localStorage.getItem('token');
      
      if (editingNote) {
        // Update existing note
        await axios.put(`${API_URL}/student-tracking/notes/${editingNote.note_id}`, values, {
          headers: { Authorization: `Bearer ${token}` }
        });
        message.success('Note updated successfully');
      } else {
        // Create new note
        await axios.post(`${API_URL}/student-tracking/student/${selectedStudent.student_id}/notes`, values, {
          headers: { Authorization: `Bearer ${token}` }
        });
        message.success('Note added successfully');
      }
      
      noteForm.resetFields();
      setEditingNote(null);
      fetchStudentNotes(selectedStudent.student_id);
      
      // Refresh note counts
      const studentIds = completedStudents.map(s => s.student_id);
      fetchNoteCounts(studentIds);
      
    } catch (error) {
      console.error('Error saving note:', error);
      message.error('Failed to save note');
    }
  };

  // Delete note
  const deleteNote = async (noteId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/student-tracking/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('Note deleted successfully');
      fetchStudentNotes(selectedStudent.student_id);
      
      // Refresh note counts
      const studentIds = completedStudents.map(s => s.student_id);
      fetchNoteCounts(studentIds);
      
    } catch (error) {
      console.error('Error deleting note:', error);
      message.error('Failed to delete note');
    }
  };

  // Open notes modal
  const handleViewNotes = (student) => {
    setSelectedStudent(student);
    setNotesModal(true);
    fetchStudentNotes(student.student_id);
  };

  // Start editing a note
  const startEditNote = (note) => {
    setEditingNote(note);
    noteForm.setFieldsValue({
      note_text: note.note_text,
      note_type: note.note_type,
      is_important: note.is_important
    });
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
    } else {
      setDateRange([]);
    }
  };

  // Fetch completed students within date range
  const fetchCompletedStudents = async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem('token');
    if (!dateRange || dateRange.length < 2) return;

    const from = dateRange[0].format('YYYY-MM-DD');
    const to = dateRange[1].format('YYYY-MM-DD');

    const response = await axios.get(
      `${API_URL}/student-tracking/classes/last?from=${from}&to=${to}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    setCompletedStudents(response.data); // ✅ only set raw data

    // Fetch note counts
    const studentIds = response.data.map(student => student.student_id);
    if (studentIds.length > 0) {
      fetchNoteCounts(studentIds);
    }
  } catch (error) {
    console.error('Error fetching completed students:', error);
    message.error('Failed to fetch completed students data');
  } finally {
    setLoading(false);
  }
};


useEffect(() => {
  let filtered = [...completedStudents];

  // 🔎 Search filter
  if (searchText) {
    const lower = searchText.toLowerCase();
    filtered = filtered.filter(student =>
      student.firstname?.toLowerCase().includes(lower) ||
      student.lastname?.toLowerCase().includes(lower) ||
      student.student_nickname?.toLowerCase().includes(lower) ||
      student.email?.toLowerCase().includes(lower) ||
      student.class_code?.toLowerCase().includes(lower) ||
      student.subject?.toLowerCase().includes(lower)
    );
  }

  // 🔎 Branch filter (applies to everyone, backend already limits admin)
  if (selectedBranch) {
    const branchIdNum = Number(selectedBranch);
    filtered = filtered.filter(student => Number(student.branch_id) === branchIdNum);
  }

  setFilteredData(filtered);
}, [completedStudents, searchText, selectedBranch]);



const handleSearch = (value) => {
  setSearchText(value);
};



  useEffect(() => {
    if (dateRange && dateRange.length === 2) {
      fetchCompletedStudents();
    }
  }, [dateRange]);

  // Calculate days since last class
  const getDaysSinceLastClass = (lastClassDate) => {
    return moment().diff(moment(lastClassDate), 'days');
  };

  // Get urgency color based on days since completion
  const getFollowUpUrgency = (daysSince) => {
    if (daysSince <= 7) {
      return { color: 'green', text: 'Fresh', priority: 'Low' };
    } else if (daysSince <= 30) {
      return { color: 'blue', text: 'Recent', priority: 'Medium' };
    } else if (daysSince <= 90) {
      return { color: 'orange', text: 'Warm', priority: 'High' };
    } else {
      return { color: 'red', text: 'Cold', priority: 'Very High' };
    }
  };

        const branchColors = {
  1: "blue",
  2: "green",
  3: "magenta", 
};

  // Table columns
  const columns = [
     {
                title: t("Branch"),
                key: "branch",
                dataIndex: "branch_name", // ✅ directly map branch_name
                width: 120,
                ellipsis: false,
                render: (_, record) => {
                  const branchId = record.branch_id;
                  const color = branchColors[branchId] || "default";
                  return record.branch_name ? (
                    <Tag color={color}>{record.branch_name}</Tag>
                  ) : (
                    <Tag>-</Tag>
                  );
                },
              },
    {
      title: 'Last Class Date',
      key: 'lastDate',
      width: 120,
      render: (_, record) => {
        const daysSince = getDaysSinceLastClass(record.last_class_date);
        return (
          <div>
            <Text strong>{moment(record.last_class_date).format('DD MMM YYYY')}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {daysSince} days ago
            </Text>
            <br />
            <Text style={{ fontSize: '10px', color: '#666' }}>
              {record.schedule_time}
            </Text>
          </div>
        );
      },
    },
    {
      title: 'Student',
      key: 'student',
      width: 300,
      render: (_, record) => {
        const noteCount = noteCounts[record.student_id];
        return (
          <div>
            <Space align="start">
              <div style={{ flex: 1 }}>
                <Text strong>{record.firstname} {record.lastname}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {record.student_nickname} | {record.email}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  <PhoneOutlined /> {record.phone}
                </Text>
              </div>
              {noteCount && (
                <div>
                  <Tooltip title={`${noteCount.notes_count} notes${noteCount.important_notes_count > 0 ? `, ${noteCount.important_notes_count} important` : ''}`}>
                    <Tag 
                      color={noteCount.important_notes_count > 0 ? 'red' : 'blue'}
                      icon={<FileTextOutlined />}
                    >
                      {noteCount.notes_count}
                    </Tag>
                  </Tooltip>
                </div>
              )}
            </Space>
          </div>
        );
      },
    },
    {
      title: 'Last Completed Course',
      key: 'lastCourse',
      render: (_, record) => (
        <div>
          <Space direction="vertical" size="small">
            <div>
              <Tag color="blue">{record.class_code}</Tag>
            </div>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              Teacher: {record.teacher}
            </Text>
          </Space>
        </div>
      ),
    },
    {
      title: 'Follow-up Priority',
      key: 'priority',
      width: 130,
      render: (_, record) => {
        const daysSince = getDaysSinceLastClass(record.last_class_date);
        const urgency = getFollowUpUrgency(daysSince);
        
        return (
          <div>
            <Tag color={urgency.color}>
              {urgency.text}
            </Tag>
            <br />
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {urgency.priority} Priority
            </Text>
          </div>
        );
      },
    },
{
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => {
        const noteCount = noteCounts[record.student_id];
        return (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Button
              type="primary"
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => handleViewNotes(record)}
              style={{ width: '100%' }}
            >
              Notes
            </Button>
            {noteCount && (
              <div style={{ textAlign: 'center' }}>
                <Text style={{ fontSize: '11px', color: '#666' }}>
                  {noteCount.notes_count} note{noteCount.notes_count !== 1 ? 's' : ''}
                  {noteCount.important_notes_count > 0 && (
                    <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                      {' '}({noteCount.important_notes_count} important)
                    </span>
                  )}
                </Text>
              </div>
            )}
            {!noteCount && (
              <div style={{ textAlign: 'center' }}>
                <Text style={{ fontSize: '11px', color: '#ccc' }}>No notes</Text>
              </div>
            )}
          </Space>
        );
      },
    }
  ];

  // Statistics calculations
  const stats = {
    total: filteredData.length,
    thisWeek: filteredData.filter(s => getDaysSinceLastClass(s.last_class_date) <= 7).length,
    thisMonth: filteredData.filter(s => getDaysSinceLastClass(s.last_class_date) <= 30).length,
    needsFollowUp: filteredData.filter(s => getDaysSinceLastClass(s.last_class_date) > 30).length,
  };

  return (
    <div>
      <Card>
        <div style={{ marginBottom: '16px' }}>
          <Title level={3}>
            <CheckCircleOutlined /> Completed Students Tracker
          </Title>
        </div>

        {/* Statistics Row */}
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col span={6}>
            <Statistic
              title="Total Completed"
              value={stats.total}
              prefix={<UserOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="This Week"
              value={stats.thisWeek}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="This Month"
              value={stats.thisMonth}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Needs Follow-up"
              value={stats.needsFollowUp}
              valueStyle={{ color: '#fa541c' }}
            />
          </Col>
        </Row>

        {/* Controls */}
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <Space wrap>
            <RangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              format="DD MMM YYYY"
              placeholder={['From Date', 'To Date']}
            />

            <Button 
              icon={<ReloadOutlined />} 
              onClick={fetchCompletedStudents}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
          {userRole === "superadmin" && (
            <Select
              placeholder={t("Select Branch")}
              style={{ width: 220 }}
              allowClear
              value={selectedBranch}
              onChange={(val) => setSelectedBranch(val)}
            >
              {branches.map((branch) => (
                <Select.Option key={branch.id} value={branch.id}>
                  {branch.name}
                </Select.Option>
              ))}
            </Select>
          )}   
          <Input
            placeholder="Search students or courses..."
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ width: 300 }}
            prefix={<SearchOutlined />}
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="student_id"
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} completed students`
          }}
          expandable={{
            expandedRowRender: (record) => (
              <div style={{ padding: '16px', backgroundColor: '#fafafa' }}>
                <Title level={5}>Student Details</Title>
                <Row gutter={24}>
                  <Col span={12}>
                    <Space direction="vertical" size="small">
                      <div>
                        <Text strong>Full Name:</Text> {record.firstname} {record.lastname}
                      </div>
                      <div>
                        <Text strong>Nickname:</Text> {record.student_nickname}
                      </div>
                      <div>
                        <Text strong>Email:</Text> {record.email}
                      </div>
                      <div>
                        <Text strong>Phone:</Text> {record.phone}
                      </div>
                    </Space>
                  </Col>
                  <Col span={12}>
                    <Space direction="vertical" size="small">
                      <div>
                        <Text strong>Last Course:</Text> {record.class_code} - {record.subject}
                      </div>
                      <div>
                        <Text strong>Teacher:</Text> {record.teacher}
                      </div>
                      <div>
                        <Text strong>Last Class:</Text> {moment(record.last_class_date).format('DD MMM YYYY')} at {record.schedule_time}
                      </div>
                      <div>
                        <Text strong>Days Since Completion:</Text> 
                        <Tag color={getFollowUpUrgency(getDaysSinceLastClass(record.last_class_date)).color} style={{ marginLeft: '8px' }}>
                          {getDaysSinceLastClass(record.last_class_date)} days
                        </Tag>
                      </div>
                    </Space>
                  </Col>
                </Row>
              </div>
            ),
            rowExpandable: () => true,
          }}
        />
      </Card>

      {/* Notes Modal */}
      <Modal
        title={
          <Space>
            <FileTextOutlined />
            Notes for {selectedStudent?.firstname} {selectedStudent?.lastname}
          </Space>
        }
        open={notesModal}
        onCancel={() => {
          setNotesModal(false);
          setSelectedStudent(null);
          setEditingNote(null);
          noteForm.resetFields();
        }}
        footer={null}
        width={800}
      >
        {selectedStudent && (
          <div>
            
            {/* Student Info */}
            <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f8f9fa' }}>
              <Row>
                <Col span={12}>
                  <Text strong>Student:</Text> {selectedStudent.firstname} {selectedStudent.lastname}<br />
                  <Text strong>Email:</Text> {selectedStudent.email}<br />
                  <Text strong>Phone:</Text> {selectedStudent.phone}
                </Col>
                <Col span={12}>
                  <Text strong>Last Course:</Text> {selectedStudent.class_code}<br />
                  <Text strong>Last Class:</Text> {moment(selectedStudent.last_class_date).format('DD MMM YYYY')}<br />
                </Col>
              </Row>
            </Card>
             {/* Notes List */}
            <Card title="Notes History" size="small" style={{ marginBottom: 16 }}>
              <List
                loading={notesLoading}
                dataSource={studentNotes}
                locale={{ emptyText: 'No notes yet' }}
                renderItem={(note) => (
                  <List.Item
                    actions={[
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => startEditNote(note)}
                      >
                        Edit
                      </Button>,
                      <Popconfirm
                        title="Delete this note?"
                        onConfirm={() => deleteNote(note.note_id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button size="small" danger icon={<DeleteOutlined />}>
                          Delete
                        </Button>
                      </Popconfirm>
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          <Tag color={
                            note.note_type === 'follow_up' ? 'blue' :
                            note.note_type === 'contact_attempt' ? 'orange' :
                            note.note_type === 'complaint' ? 'red' :
                            note.note_type === 'feedback' ? 'green' :
                            note.note_type === 'referral' ? 'purple' : 'default'
                          }>
                            {note.note_type.replace('_', ' ').toUpperCase()}
                          </Tag>
                          {note.is_important && (
                            <Tag color="red" icon={<ExclamationCircleOutlined />}>
                              IMPORTANT
                            </Tag>
                          )}
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            by {note.created_by_name} • {moment(note.created_at).format('DD MMM YYYY, HH:mm')}
                          </Text>
                        </Space>
                      }
                      description={
                        <div style={{ whiteSpace: 'pre-wrap', marginTop: '8px' }}>
                          {note.note_text}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>

            {/* Add/Edit Note Form */}
            <Card 
              title={editingNote ? "Edit Note" : "Add New Note"} 
              size="small" 
              style={{ marginBottom: 16 }}
            >
              <Form
                form={noteForm}
                onFinish={saveNote}
                layout="vertical"
              >
                <Row gutter={16}>
                  <Col span={16}>
                    <Form.Item
                      name="note_text"
                      label="Note"
                      rules={[{ required: true, message: 'Please enter note text' }]}
                    >
                      <Input.TextArea 
                        rows={3} 
                        placeholder="Enter your note here..."
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="note_type" label="Type" initialValue="general">
                      <Select>
                        <Select.Option value="general">General</Select.Option>
                        <Select.Option value="follow_up">Follow Up</Select.Option>
                        <Select.Option value="contact_attempt">Contact Attempt</Select.Option>
                        <Select.Option value="feedback">Feedback</Select.Option>
                        <Select.Option value="complaint">Complaint</Select.Option>
                        <Select.Option value="referral">Referral</Select.Option>
                      </Select>
                    </Form.Item>
                    <Form.Item name="is_important" valuePropName="checked">
                      <Checkbox>Mark as Important</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit">
                      {editingNote ? 'Update Note' : 'Add Note'}
                    </Button>
                    {editingNote && (
                      <Button onClick={() => {
                        setEditingNote(null);
                        noteForm.resetFields();
                      }}>
                        Cancel Edit
                      </Button>
                    )}
                  </Space>
                </Form.Item>
              </Form>
            </Card>

           
          </div>
        )}
      </Modal>
    </div>
  );
};

export default withRoleAccess(CompletedStudentsTracker, ['admin', 'superadmin']);